import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  Tag,
  Avatar,
  notification,
  Tooltip,
  Button,
  Input,
  Table,
  Select,
  Modal,
  Divider,
  Switch,
  InputNumber,
  Popconfirm,
  DatePicker,
  Menu,
  Dropdown,
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import moment from 'moment';
import dayjs from 'dayjs';
import { CheckCircleOutlined } from '@ant-design/icons';
import { businessAtom, timezoneAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import { renderShortDate, renderHour, toCurrency } from '../../utils/functions';
import envConfig from '../../../envConfig';
import { businessesSelector } from '../../../atoms/Selectors';

function PaypalInvoices() {
  const { Option } = Select;
  const business = useRecoilValue(businessAtom);
  const timezone = useRecoilValue(timezoneAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const { t } = useTranslation();
  const [lastSearch, setLastSearch] = useState('');
  const [size, setSize] = useState(20);
  const [filterStatus, setFilterStatus] = useState(0);
  const [loading, setLoading] = useState(false);
  const businesses = useRecoilValueLoadable(businessesSelector);
  const [selectedBusiness, setSelectedBusiness] = useState(business?.id);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [result, setResult] = useState({
    total: 0,
    invoices: []
  });

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function search(page, query) {
    if (!loading) {
      setLastSearch(query);
      setLoading(true);
      api.post('paypalinvoices/search', {
        businessId: selectedBusiness,
        query,
        page,
        size,
        searchType: filterStatus
      })
        .then((response) => {
          if (response.data.error) {
            showMessage(response.data.error);
          } else {
            setResult(response.data.data);
          }
        })
        .catch((error) => {
          showMessage(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function createNewInvoice() {
    setLoading(true);
    api.post('paypalinvoices', {
      ...selectedInvoice,
      BusinessId: selectedBusiness,
      PaymentTermDueDate: selectedInvoice?.paymentTermDueDate ? dayjs(selectedInvoice.paymentTermDueDate).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null
    })
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          search(currentPage, lastSearch);
          setShowCreateInvoice(false);
          setSelectedInvoice(response.data.data);
          setShowInvoice(true);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function updateInvoice() {
    setLoading(true);
    api.put('paypalinvoices', {
      ...selectedInvoice,
      BusinessId: selectedBusiness,
      PaymentTermDueDate: selectedInvoice?.paymentTermDueDate ? dayjs(selectedInvoice.paymentTermDueDate).add(-1 * envConfig.REACT_APP_TIMEZONE_OFFSET, 'hours').format('YYYY-MM-DDTHH:mm:00.000') : null
    })
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          search(currentPage, lastSearch);
          setSelectedInvoice(response.data.data);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function cancelInvoice() {
    setLoading(true);
    api.post(`paypalinvoices/cancel/${selectedInvoice?.id}`, null)
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          search(currentPage, lastSearch);
          setSelectedInvoice(response.data.data);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function deleteInvoice() {
    setLoading(true);
    api.delete(`paypalinvoices/delete/${selectedInvoice?.id}`)
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          search(currentPage, lastSearch);
          setSelectedInvoice(response.data.data);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function sendInvoice() {
    setLoading(true);
    api.post(`paypalinvoices/send/${selectedInvoice?.id}`, null)
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          search(currentPage, lastSearch);
          setSelectedInvoice(response.data.data);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error.message);
      });
  }

  function getInvoicePayments() {
    setLoadingPayments(true);
    api.get(`paypalinvoices/payments?invoiceId=${selectedInvoice?.id}`)
      .then((response) => {
        if (response.data.error) {
          setLoading(false);
          showMessage(response.data.error);
        } else {
          setInvoicePayments(response.data.data.results);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      }).finally(() => {
        setLoadingPayments(false);
      });
  }

  function searchUsers() {
    setLoadingUsers(true);
    api
      .post('users/search', { SearchText: userSearchText })
      .then((response) => {
        setLoadingUsers(false);
        if (response?.data?.success) {
          setUsers(response.data.data.results);
        } else {
          showMessage(response?.data?.error);
        }
      })
      .catch((error) => {
        setLoadingUsers(false);
        showMessage(error.message);
      });
  }

  function allowSubmitInvoice() {
    let shouldAllow = true;
    // if (!selectedInvoice?.paypalInvoiceReferenceNumber || selectedInvoice?.paypalInvoiceReferenceNumber?.length === 0) {
    //   shouldAllow = false;
    // }
    if (selectedInvoice?.paymentTermType === 'DUE_ON_DATE_SPECIFIED' && !selectedInvoice?.paymentTermDueDate) {
      shouldAllow = false;
    }
    if (selectedInvoice?.allowPartialPayments && !selectedInvoice?.partialPaymentMinimum <= 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.recipientFirstName || selectedInvoice?.recipientFirstName?.length === 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.recipientLastName || selectedInvoice?.recipientLastName?.length === 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.recipientEmail || selectedInvoice?.recipientEmail?.length === 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.recipientPhoneNumber || selectedInvoice?.recipientPhoneNumber?.length === 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.itemName || selectedInvoice?.itemName?.length === 0) {
      shouldAllow = false;
    }
    if (!selectedInvoice?.itemDescription || selectedInvoice?.itemDescription?.length === 0) {
      shouldAllow = false;
    }
    if (selectedInvoice?.itemAmount <= 0) {
      shouldAllow = false;
    }
    if (selectedInvoice?.itemPrice <= 0) {
      shouldAllow = false;
    }
    return shouldAllow;
  }

  function getMappedInvoice(invoice) {
    const extendedPrice = (invoice.itemPrice * invoice.itemAmount).roundTo(2);
    let temp = {
      ...invoice,
      munPct: 0,
      statePct: 0,
      reducedPct: 0,
    };
    if (invoice.itemMunicipalTax > 0) {
      temp = {
        ...temp,
        munPct: (invoice.itemMunicipalTax / extendedPrice).roundTo(3)
      };
    }
    if (invoice.itemStateTax > 0) {
      temp = {
        ...temp,
        statePct: (invoice.itemStateTax / extendedPrice).roundTo(3)
      };
    }
    if (invoice.itemReducedTax > 0) {
      temp = {
        ...temp,
        reducedPct: (invoice.itemReducedTax / extendedPrice).roundTo(3)
      };
    }
    return temp;
  }

  function getStatusColor(status) {
    let color = '';
    if (status === 1 || status === 4) {
      color = 'green';
    } else if (status === 2) {
      color = 'orange';
    } else if (status === 3) {
      color = 'red';
    }
    return color;
  }

  function getStatusText(status) {
    let statusText = 'draft';
    if (status === 1) {
      statusText = 'sent';
    } else if (status === 2) {
      statusText = 'cancelled';
    } else if (status === 3) {
      statusText = 'deleted';
    } else if (status === 4) {
      statusText = 'completed';
    }
    return t(statusText);
  }

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, [size, filterStatus]);

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, []);

  useEffect(() => {
    if (business) {
      setSelectedBusiness(business.id);
    } else {
      setSelectedBusiness('');
    }
  }, [business]);

  useEffect(() => {
    setCurrentPage(0);
    search(0, lastSearch);
  }, [selectedBusiness]);

  useEffect(() => {
    setSelectedInvoice({
      ...selectedInvoice,
      itemMunicipalTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * selectedInvoice?.munPct).roundTo(2),
      itemStateTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * selectedInvoice?.statePct).roundTo(2),
      itemReducedTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * selectedInvoice?.reducedPct).roundTo(2)
    });
  }, [selectedInvoice?.itemPrice, selectedInvoice?.itemAmount]);

  useEffect(() => {
    if (showInvoice && selectedInvoice) {
      getInvoicePayments();
    } else if (!showInvoice && !selectedInvoice) {
      setInvoicePayments([]);
    }
  }, [showInvoice, selectedInvoice]);

  return (
    <div>
      <PageHeader
        title={t('paypal_invoices')}
        tags={
          <Tag
            color="#2db7f5"
            icon={<CheckCircleOutlined />}
          >
            {business ? business.name : t('all_stores')}
          </Tag>
        }
        extra={[
          <Button
            type="primary"
            onClick={() => {
              search(currentPage, lastSearch);
            }}
          >
            {t('reload')}
          </Button>,
          <Button
            type="primary"
            disabled={loading}
            onClick={() => {
              setSelectedInvoice({
                paymentTermType: 'DUE_ON_RECEIPT',
                allowPartialPayments: false,
                partialPaymentMinimum: 0,
                paymentTermDueDate: null,
                itemAmount: 1,
                itemPrice: 0,
                itemMunicipalTax: 0,
                itemStateTax: 0,
                itemReducedTax: 0,
                munPct: 0.01,
                statePct: 0.105,
                reducedPct: 0.06,
              });
              setShowCreateInvoice(true);
            }}
          >
            {t('create')}
          </Button>
        ]}
      />
      <div className="flex space-x-2">
        <Input.Search
          className="w-full mb-1 mt-5"
          allowClear
          loading={loading}
          enterButton={t('search')}
          onSearch={(value) => {
            setCurrentPage(0);
            search(0, value);
          }}
        />
        {!business?.id && (
          <div>
            <p className="font-bold">{t('business')}:</p>
            <Select
              loading={businesses?.state === 'loading'}
              disabled={businesses?.state === 'loading' || businesses?.contents?.data?.data?.results?.length === 0}
              className="w-48"
              value={selectedBusiness}
              onChange={(value) => setSelectedBusiness(value)}
            >
              <Option
                key="any"
                value=""
                className="text-xs flex items-center"
              >
                {t('any')}
              </Option>
              {businesses.state === 'hasValue' && (
                <>
                  {_.map(_.sortBy(businesses.contents.data.data.results, (s) => s.name, 'asc'), (b) => (
                    <Option
                      value={b.id}
                      key={b.id}
                    >
                      {b.name}
                    </Option>
                  ))}
                </>
              )}
            </Select>
          </div>
        )}
        <div>
          <p className="font-bold">{t('status')}:</p>
          <Select
            disabled={loading}
            className="w-48"
            value={filterStatus}
            onChange={(value) => setFilterStatus(value)}
          >
            <Option
              key="any"
              value={0}
              className="text-xs flex items-center"
            >
              {t('all')}
            </Option>
            <Option
              key="draft"
              value={1}
              className="text-xs flex items-center"
            >
              {t('draft')}
            </Option>
            <Option
              key="sent"
              value={2}
              className="text-xs flex items-center"
            >
              {t('sent')}
            </Option>
            <Option
              key="sentpayments"
              value={3}
              className="text-xs flex items-center"
            >
              {t('sent_with_payments')}
            </Option>
            <Option
              key="sentnopayments"
              value={4}
              className="text-xs flex items-center"
            >
              {t('sent_without_payments')}
            </Option>
            <Option
              key="cancelled"
              value={5}
              className="text-xs flex items-center"
            >
              {t('cancelled')}
            </Option>
            <Option
              key="deleted"
              value={5}
              className="text-xs flex items-center"
            >
              {t('deleted')}
            </Option>
            <Option
              key="completed"
              value={5}
              className="text-xs flex items-center"
            >
              {t('completed')}
            </Option>
          </Select>
        </div>
      </div>
      <Table
        bordered
        loading={loading}
        dataSource={result.invoices}
        pagination={{
          pageSize: size,
          showSizeChanger: true,
          defaultCurrent: 0,
          current: currentPage + 1,
          total: result?.total,
          onChange: (page, pageSize) => {
            if (page - 1 !== currentPage) {
              setCurrentPage(page - 1);
              search(page - 1, lastSearch);
            } else {
              setSize(pageSize);
            }
          }
        }}
        columns={[
          {
            title: '',
            key: 'mobile',
            align: 'center',
            className: 'text-xs md:hidden',
            render: (row) => (
              <div className="grid grid-cols-2 gap-4 shadow-xl p-2">
                <div className="col-span-1 text-left">
                  <Tag color={getStatusColor(row?.status)}>{getStatusText(row?.status)}</Tag>
                </div>
                <div className="col-span-1 text-right">
                  <p className="font-bold">{t('invoice_number')}</p>
                  <p>{row?.paypalInvoiceNumber}</p>
                </div>
                <div className="col-span-1 text-left">
                  <p className="font-bold">{t('reference_number')}</p>
                  <p>{row?.paypalInvoiceReferenceNumber}</p>
                </div>
                <div className="col-span-1 text-right">
                  <p className="font-bold">{t('total')}</p>
                  <p>{row?.invoiceTotal}</p>
                </div>
                <div className="col-span-2 text-center">
                  <p className="font-bold">{t('recipient')}</p>
                  <p>{row?.recipientCompleteName}</p>
                  <p>{row?.recipientEmail}</p>
                  <p>{row?.recipientPhoneNumber}</p>
                </div>
                <div className="col-span-2 text-center">
                  <p className="font-bold">{t('recipient')}</p>
                  <p>{row?.itemName}</p>
                  <p>{row?.itemDescription}</p>
                </div>
                <Button
                  className="col-span-2"
                  size="small"
                  type="primary"
                  OnClick={() => {
                    setSelectedInvoice(getMappedInvoice(row));
                    setShowInvoice(true);
                  }}
                >
                  {t('view')}
                </Button>
              </div>
            ),
          },
          {
            title: t('invoice_number'),
            key: 'code',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.paypalInvoiceNumber}</span>
            ),
          },
          {
            title: t('reference_number'),
            key: 'code',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{row?.paypalInvoiceReferenceNumber}</span>
            ),
          },
          {
            title: t('status'),
            key: 'status',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <Tag color={getStatusColor(row?.status)}>{getStatusText(row?.status)}</Tag>
            ),
          },
          {
            title: t('recipient'),
            key: 'user',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <div>
                <p>{row?.recipientCompleteName}</p>
                <p>{row?.recipientEmail}</p>
                <p>{row?.recipientPhoneNumber}</p>
              </div>
            ),
          },
          {
            title: t('description'),
            key: 'description',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <div>
                <p>{row?.itemName}</p>
                <p>{row?.itemDescription}</p>
              </div>
            ),
          },
          {
            title: t('total'),
            key: 'total',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <span>{toCurrency(row?.invoiceTotal)}</span>
            ),
          },
          {
            title: t('actions'),
            key: 'actions',
            align: 'center',
            className: 'hidden md:table-cell text-xs',
            render: (row) => (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setSelectedInvoice(getMappedInvoice(row));
                  setShowInvoice(true);
                }}
              >
                {t('view')}
              </Button>
            ),
          },
        ]}
      />
      <Modal
        title={t('create_invoice')}
        width="85%"
        open={showCreateInvoice}
        onCancel={() => {
          if (!loading) {
            setShowCreateInvoice(false);
            setSelectedInvoice(null);
          }
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            danger
            disabled={loading}
            onClick={() => {
              if (!loading) {
                setShowCreateInvoice(false);
                setSelectedInvoice(null);
              }
            }}
          >
            {t('close')}
          </Button>,
          <Button
            key="ok"
            type="primary"
            onClick={() => createNewInvoice()}
            loading={loading}
            disabled={loading || !allowSubmitInvoice()}
          >
            {t('create')}
          </Button>
        ]}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('basic_info')}</Divider>
          <div className="col-span-1 md:col-span-2">
            <p>
              <span className="font-bold">{t('reference_number')}</span> <span className="italic text-gray-600 text-xs">({t('optional_leave_blank_for_autogenerate')})</span>
            </p>
            <Input
              value={selectedInvoice?.paypalInvoiceReferenceNumber}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                paypalInvoiceReferenceNumber: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('payment_term')}</p>
            <Select
              className="w-full"
              disabled={loading}
              value={selectedInvoice?.paymentTermType}
              onChange={(obj) => {
                let temp = {
                  ...selectedInvoice,
                  paymentTermType: obj
                };
                if (obj !== 'DUE_ON_DATE_SPECIFIED') {
                  temp = {
                    ...temp,
                    paymentTermDueDate: null
                  };
                }
                setSelectedInvoice(temp);
              }}
            >
              <Option value="DUE_ON_RECEIPT">{t('due_on_receipt')}</Option>
              <Option value="DUE_ON_DATE_SPECIFIED">{t('due_on_date')}</Option>
              <Option value="NET_10">{t('net_10')}</Option>
              <Option value="NET_15">{t('net_15')}</Option>
              <Option value="NET_30">{t('net_30')}</Option>
              <Option value="NET_45">{t('net_45')}</Option>
              <Option value="NET_60">{t('net_60')}</Option>
              <Option value="NET_90">{t('net_90')}</Option>
              <Option value="NO_DUE_DATE">{t('no_due_date')}</Option>
            </Select>
          </div>
          {selectedInvoice?.paymentTermType === 'DUE_ON_DATE_SPECIFIED' && (
            <div className="col-span-1">
              <p className="font-bold">{t('due_date')}</p>
              <DatePicker
                allowClear={false}
                disabled={loading}
                className="w-full"
                format="YYYY-MM-DD hh:mm A"
                value={selectedInvoice?.paymentTermDueDate ? dayjs(selectedInvoice?.paymentTermDueDate) : null}
                onChange={(date, dateString) => {
                  setSelectedInvoice({
                    ...selectedInvoice,
                    paymentTermDueDate: dayjs(dateString)
                  });
                }}
              />
            </div>
          )}
          {selectedInvoice?.paymentTermType !== 'DUE_ON_DATE_SPECIFIED' && (
            <div className="col-span-1" />
          )}
          <div className="col-span-1">
            <p className="font-bold">{t('allow_partial_payments')}</p>
            <Switch
              value={selectedInvoice?.allowPartialPayments}
              disabled={loading}
              onChange={(obj) => {
                let temp = {
                  ...selectedInvoice,
                  allowPartialPayments: obj
                };
                if (!obj) {
                  temp = {
                    ...temp,
                    partialPaymentMinimum: 0
                  };
                }
                setSelectedInvoice(temp);
              }}
            />
          </div>
          {selectedInvoice?.allowPartialPayments && (
            <div className="col-span-1">
              <p className="font-bold">{t('partial_payment_minimum')}</p>
              <InputNumber
                value={selectedInvoice?.partialPaymentMinimum}
                disabled={loading}
                step="0.01"
                addonBefore="$"
                className="w-full"
                onChange={(obj) => {
                  setSelectedInvoice({
                    ...selectedInvoice,
                    partialPaymentMinimum: obj
                  });
                }}
              />
            </div>
          )}

          <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('recipient')}</Divider>
          <div className="col-span-1 md:col-span-2 lg:col-span-4">
            <p className="font-bold">{t('select_user')}</p>
            <Dropdown
              trigger="click"
              placement="bottom"
              overlay={
                <Menu>
                  {_.map(users, (u) => (
                    <Menu.Item
                      key={u.id}
                      onClick={() => {
                        setSelectedInvoice({
                          ...selectedInvoice,
                          recipientFirstName: u.firstName,
                          recipientLastName: u.lastName,
                          recipientEmail: u.email,
                          recipientPhoneNumber: u.phoneNumber
                        });
                        setUsers([]);
                        setUserSearchText('');
                      }}
                    >
                      {u.completeName}: {u.email}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Input.Search
                className="w-full"
                allowClear
                loading={loadingUsers}
                disabled={loading}
                value={userSearchText}
                onChange={(obj) => {
                  setUserSearchText(obj.target.value);
                }}
                onSearch={() => searchUsers()}
                enterButton={t('search')}
              />
            </Dropdown>
            <p className="text-lg text-center my-2">{t('or')}</p>
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('first_name')}</p>
            <Input
              disabled={loading}
              value={selectedInvoice?.recipientFirstName}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                recipientFirstName: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('last_name')}</p>
            <Input
              value={selectedInvoice?.recipientLastName}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                recipientLastName: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('email')}</p>
            <Input
              value={selectedInvoice?.recipientEmail}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                recipientEmail: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('phone')}</p>
            <Input
              value={selectedInvoice?.recipientPhoneNumber}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                recipientPhoneNumber: obj.target.value
              })}
            />
          </div>
          <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('payment_details')}</Divider>
          <div className="col-span-1">
            <p className="font-bold">{t('name')}</p>
            <Input
              value={selectedInvoice?.itemName}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                itemName: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('description')}</p>
            <Input
              value={selectedInvoice?.itemDescription}
              disabled={loading}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                itemDescription: obj.target.value
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('amount')}</p>
            <InputNumber
              step="1"
              className="w-full"
              disabled={loading}
              value={selectedInvoice?.itemAmount}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                itemAmount: obj
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('price')}</p>
            <InputNumber
              step="0.01"
              addonBefore="$"
              className="w-full"
              disabled={loading}
              value={selectedInvoice?.itemPrice}
              onChange={(obj) => setSelectedInvoice({
                ...selectedInvoice,
                itemPrice: obj
              })}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('municipal_tax')}</p>
            <InputNumber
              step="0.01"
              addonAfter="%"
              className="w-full"
              disabled={loading}
              value={(selectedInvoice?.munPct * 100).roundTo(2)}
              onChange={(obj) => {
                const tempVal = (obj / 100).roundTo(4);
                setSelectedInvoice({
                  ...selectedInvoice,
                  itemMunicipalTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                  munPct: tempVal
                });
              }}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('state_tax')}</p>
            <InputNumber
              step="0.01"
              addonAfter="%"
              className="w-full"
              disabled={loading}
              value={(selectedInvoice?.statePct * 100).roundTo(2)}
              onChange={(obj) => {
                const tempVal = (obj / 100).roundTo(4);
                setSelectedInvoice({
                  ...selectedInvoice,
                  itemStateTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                  statePct: tempVal
                });
              }}
            />
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('reduced_tax')}</p>
            <InputNumber
              step="0.01"
              addonAfter="%"
              className="w-full"
              disabled={loading}
              value={(selectedInvoice?.reducedPct * 100).roundTo(2)}
              onChange={(obj) => {
                const tempVal = (obj / 100).roundTo(4);
                setSelectedInvoice({
                  ...selectedInvoice,
                  itemReducedTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                  reducedPct: tempVal
                });
              }}
            />
          </div>
          <div className="col-span-1 md:col-span-2 lg:col-span-4 text-right">
            <p><span className="font-bold">{t('subtotal')}</span>: {(selectedInvoice?.itemPrice * selectedInvoice?.itemAmount).roundTo(2)}</p>
            <p><span className="font-bold">{t('tax_total')}</span>: {(selectedInvoice?.itemMunicipalTax + selectedInvoice?.itemStateTax + selectedInvoice?.itemReducedTax).roundTo(2)}</p>
            <p><span className="font-bold">{t('total')}</span>: {((selectedInvoice?.itemPrice * selectedInvoice?.itemAmount).roundTo(2) + selectedInvoice?.itemMunicipalTax + selectedInvoice?.itemStateTax + selectedInvoice?.itemReducedTax).roundTo(2)}</p>
          </div>
        </div>
      </Modal>
      <Modal
        width="85%"
        title={t('update_invoice')}
        open={showInvoice}
        onCancel={() => {
          if (!loading) {
            setShowInvoice(false);
            setSelectedInvoice(null);
          }
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            danger
            disabled={loading}
            onClick={() => {
              if (!loading) {
                setShowInvoice(false);
                setSelectedInvoice(null);
              }
            }}
          >
            {t('close')}
          </Button>,
          <Popconfirm
            key="sendconfirm"
            okText={t('yes')}
            cancelText={t('no')}
            title={selectedInvoice?.status === 0 ? t('confirm_send_invoice_prompt') : t('confirm_remind_invoice_prompt')}
            onConfirm={() => sendInvoice()}
          >
            <Button
              key="send"
              type="primary"
              loading={loading}
              disabled={loading || (selectedInvoice?.status !== 0 && selectedInvoice?.status !== 1)}
            >
              {selectedInvoice?.status === 0 ? t('send') : t('resend')}
            </Button>
          </Popconfirm>,
          <Popconfirm
            key="cancelconfirm"
            okText={t('yes')}
            cancelText={t('no')}
            title={t('confirm_cancel_invoice_prompt')}
            onConfirm={() => cancelInvoice()}
          >
            <Button
              key="cancel"
              danger
              type="primary"
              loading={loading}
              disabled={loading || selectedInvoice?.status !== 1}
            >
              {t('cancel')}
            </Button>
          </Popconfirm>,
          <Popconfirm
            key="deleteconfirm"
            okText={t('yes')}
            cancelText={t('no')}
            title={t('confirm_delete_invoice_prompt')}
            onConfirm={() => deleteInvoice()}
          >
            <Button
              key="delete"
              danger
              type="primary"
              loading={loading}
              disabled={loading || (selectedInvoice?.status !== 0 && selectedInvoice?.status !== 2)}
            >
              {t('delete')}
            </Button>
          </Popconfirm>,
          <Button
            key="ok"
            type="primary"
            onClick={() => updateInvoice()}
            loading={loading}
            disabled={loading || !allowSubmitInvoice()
              || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
          >
            {t('update')}
          </Button>
        ]}
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 divide-x">
          <div className="col-span-1 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pr-2">
            <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('basic_info')}</Divider>
            <div className="col-span-1 md:col-span-2">
              <p>
                <span className="font-bold">{t('reference_number')}</span> <span className="italic text-gray-600 text-xs">({t('optional_leave_blank_for_autogenerate')})</span>
              </p>
              <Input
                value={selectedInvoice?.paypalInvoiceReferenceNumber}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  paypalInvoiceReferenceNumber: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('payment_term')}</p>
              <Select
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={selectedInvoice?.paymentTermType}
                onChange={(obj) => {
                  let temp = {
                    ...selectedInvoice,
                    paymentTermType: obj
                  };
                  if (obj !== 'DUE_ON_DATE_SPECIFIED') {
                    temp = {
                      ...temp,
                      paymentTermDueDate: null
                    };
                  }
                  setSelectedInvoice(temp);
                }}
              >
                <Option value="DUE_ON_RECEIPT">{t('due_on_receipt')}</Option>
                <Option value="DUE_ON_DATE_SPECIFIED">{t('due_on_date')}</Option>
                <Option value="NET_10">{t('net_10')}</Option>
                <Option value="NET_15">{t('net_15')}</Option>
                <Option value="NET_30">{t('net_30')}</Option>
                <Option value="NET_45">{t('net_45')}</Option>
                <Option value="NET_60">{t('net_60')}</Option>
                <Option value="NET_90">{t('net_90')}</Option>
                <Option value="NO_DUE_DATE">{t('no_due_date')}</Option>
              </Select>
            </div>
            {selectedInvoice?.paymentTermType === 'DUE_ON_DATE_SPECIFIED' && (
              <div className="col-span-1">
                <p className="font-bold">{t('due_date')}</p>
                <DatePicker
                  allowClear={false}
                  disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                  className="w-full"
                  format="YYYY-MM-DD hh:mm A"
                  value={selectedInvoice?.paymentTermDueDate ? dayjs(selectedInvoice?.paymentTermDueDate) : null}
                  onChange={(date, dateString) => {
                    setSelectedInvoice({
                      ...selectedInvoice,
                      paymentTermDueDate: dayjs(dateString)
                    });
                  }}
                />
              </div>
            )}
            {selectedInvoice?.paymentTermType !== 'DUE_ON_DATE_SPECIFIED' && (
              <div className="col-span-1" />
            )}
            <div className="col-span-1">
              <p className="font-bold">{t('allow_partial_payments')}</p>
              <Switch
                value={selectedInvoice?.allowPartialPayments}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => {
                  let temp = {
                    ...selectedInvoice,
                    allowPartialPayments: obj
                  };
                  if (!obj) {
                    temp = {
                      ...temp,
                      partialPaymentMinimum: 0
                    };
                  }
                  setSelectedInvoice(temp);
                }}
              />
            </div>
            {selectedInvoice?.allowPartialPayments && (
              <div className="col-span-1">
                <p className="font-bold">{t('partial_payment_minimum')}</p>
                <InputNumber
                  value={selectedInvoice?.partialPaymentMinimum}
                  disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                  step="0.01"
                  addonBefore="$"
                  className="w-full"
                  onChange={(obj) => {
                    setSelectedInvoice({
                      ...selectedInvoice,
                      partialPaymentMinimum: obj
                    });
                  }}
                />
              </div>
            )}

            <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('recipient')}</Divider>
            <div className="col-span-1">
              <p className="font-bold">{t('first_name')}</p>
              <Input
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={selectedInvoice?.recipientFirstName}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  recipientFirstName: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('last_name')}</p>
              <Input
                value={selectedInvoice?.recipientLastName}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  recipientLastName: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('email')}</p>
              <Input
                value={selectedInvoice?.recipientEmail}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  recipientEmail: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('phone')}</p>
              <Input
                value={selectedInvoice?.recipientPhoneNumber}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  recipientPhoneNumber: obj.target.value
                })}
              />
            </div>
            <Divider orientation="left" className="col-span-1 md:col-span-2 lg:col-span-4">{t('payment_details')}</Divider>
            <div className="col-span-1">
              <p className="font-bold">{t('name')}</p>
              <Input
                value={selectedInvoice?.itemName}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  itemName: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('description')}</p>
              <Input
                value={selectedInvoice?.itemDescription}
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  itemDescription: obj.target.value
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('amount')}</p>
              <InputNumber
                step="1"
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={selectedInvoice?.itemAmount}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  itemAmount: obj
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('price')}</p>
              <InputNumber
                step="0.01"
                addonBefore="$"
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={selectedInvoice?.itemPrice}
                onChange={(obj) => setSelectedInvoice({
                  ...selectedInvoice,
                  itemPrice: obj
                })}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('municipal_tax')}</p>
              <InputNumber
                step="0.01"
                addonAfter="%"
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={(selectedInvoice?.munPct * 100).roundTo(2)}
                onChange={(obj) => {
                  const tempVal = (obj / 100).roundTo(4);
                  setSelectedInvoice({
                    ...selectedInvoice,
                    itemMunicipalTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                    munPct: tempVal
                  });
                }}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('state_tax')}</p>
              <InputNumber
                step="0.01"
                addonAfter="%"
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={(selectedInvoice?.statePct * 100).roundTo(2)}
                onChange={(obj) => {
                  const tempVal = (obj / 100).roundTo(4);
                  setSelectedInvoice({
                    ...selectedInvoice,
                    itemStateTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                    statePct: tempVal
                  });
                }}
              />
            </div>
            <div className="col-span-1">
              <p className="font-bold">{t('reduced_tax')}</p>
              <InputNumber
                step="0.01"
                addonAfter="%"
                className="w-full"
                disabled={loading || selectedInvoice?.status === 1 || selectedInvoice?.status === 3 || selectedInvoice?.status === 4}
                value={(selectedInvoice?.reducedPct * 100).roundTo(2)}
                onChange={(obj) => {
                  const tempVal = (obj / 100).roundTo(4);
                  setSelectedInvoice({
                    ...selectedInvoice,
                    itemReducedTax: (selectedInvoice?.itemPrice * selectedInvoice?.itemAmount * tempVal).roundTo(2),
                    reducedPct: tempVal
                  });
                }}
              />
            </div>
            <div className="col-span-1 md:col-span-2 lg:col-span-4 text-right">
              <p><span className="font-bold">{t('subtotal')}</span>: {(selectedInvoice?.itemPrice * selectedInvoice?.itemAmount).roundTo(2)}</p>
              <p><span className="font-bold">{t('tax_total')}</span>: {(selectedInvoice?.itemMunicipalTax + selectedInvoice?.itemStateTax + selectedInvoice?.itemReducedTax).roundTo(2)}</p>
              <p><span className="font-bold">{t('total')}</span>: {((selectedInvoice?.itemPrice * selectedInvoice?.itemAmount).roundTo(2) + selectedInvoice?.itemMunicipalTax + selectedInvoice?.itemStateTax + selectedInvoice?.itemReducedTax).roundTo(2)}</p>
            </div>
          </div>
          <div className="pl-2 col-span-1 lg:col-span-2">
            <Divider orientation="left">{t('payments')}</Divider>
            <Table
              size="small"
              bordered
              dataSource={invoicePayments}
              loading={loadingPayments}
              columns={[
                {
                  title: t('date'),
                  key: 'date',
                  align: 'center',
                  className: 'hidden md:table-cell text-xs',
                  render: (row) => (
                    <div>
                      <p>{renderShortDate(row.createdAt, timezone)}</p>
                      <p>{renderHour(row.createdAt, timezone)}</p>
                    </div>
                  ),
                },
                {
                  title: t('payment_id'),
                  key: 'id',
                  align: 'center',
                  className: 'hidden md:table-cell text-xs',
                  render: (row) => (
                    <span>{row.paypalPaymentId}</span>
                  ),
                },
                {
                  title: t('amount'),
                  key: 'amount',
                  align: 'center',
                  className: 'hidden md:table-cell text-xs',
                  render: (row) => (
                    <div className="flex space-x-2 justify-center">
                      {row.amount > 0 && (
                        <Tooltip title={t('paid')}>
                          <Tag color="green">{toCurrency(row?.amount)}</Tag>
                        </Tooltip>
                      )}
                      {row.refundedAmount > 0 && (
                        <Tooltip title={t('refund')}>
                          <Tag color="red">{toCurrency(row?.refundedAmount)}</Tag>
                        </Tooltip>
                      )}
                    </div>
                  ),
                },
                {
                  title: t('is_external'),
                  key: 'id',
                  align: 'center',
                  className: 'hidden md:table-cell text-xs',
                  render: (row) => (
                    <Tag>{row.processedExternally ? t('yes') : t('no')}</Tag>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PaypalInvoices;
